import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@/components/site/Header';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';

const BREADCRUMB = [
  { name: 'Start', path: '/' },
  { name: 'AGB', path: '/agb' },
];

const Section = ({ title, children, id }) => (
  <section id={id} className="py-8 border-t border-slate-200/70">
    <h2 className="font-display font-black text-slate-900 text-xl tracking-tight mb-4">{title}</h2>
    <div className="text-slate-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

const ListItem = ({ num, children }) => (
  <li className="flex gap-3">
    <span className="font-mono-tech text-slate-500 text-[12px] pt-0.5 shrink-0">{num}</span>
    <span>{children}</span>
  </li>
);

export default function AGB() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#f6f7f9] text-slate-900 min-h-screen" data-testid="agb-page">
      <Seo
        title="Allgemeine Geschäftsbedingungen"
        description="AGB von MIBAX Messtechnik für die Erbringung von Ultraschall-Mess- und Energieeffizienzdienstleistungen. B2B, Mindestlaufzeit 24 Monate, Gerichtsstand München."
        path="/agb"
        breadcrumb={BREADCRUMB}
      />
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0891b2] mb-8" data-testid="agb-back-link">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>

          <div className="eyebrow mb-5">Rechtliche Hinweise</div>
          <h1 className="font-display font-black text-slate-900 tracking-tighter text-5xl sm:text-6xl leading-[0.95] mb-3">
            Allgemeine Geschäfts­bedingungen
          </h1>
          <p className="text-slate-500 text-base">
            AGB für die Erbringung von Mess- und Energieeffizienz­dienstleistungen.
            Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}.
          </p>

          <Section title="§ 1 Geltungsbereich" id="par-1">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Diese Allgemeinen Geschäftsbedingungen („AGB") gelten für sämtliche
                Geschäftsbeziehungen zwischen MIBAX Messtechnik, Inhaber M. Basic, Tal 44,
                80331 München („Auftragnehmer") und seinen Vertragspartnern („Auftraggeber").
              </ListItem>
              <ListItem num="(2)">
                Die AGB gelten ausschließlich gegenüber Unternehmern im Sinne von § 14 BGB,
                juristischen Personen des öffentlichen Rechts oder öffentlich-rechtlichen
                Sondervermögen. Ein Vertragsschluss mit Verbrauchern (§ 13 BGB) ist
                ausgeschlossen.
              </ListItem>
              <ListItem num="(3)">
                Entgegenstehende oder von diesen AGB abweichende Bedingungen des Auftraggebers
                erkennt der Auftragnehmer nicht an, es sei denn, deren Geltung wird ausdrücklich
                schriftlich zugestimmt.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 2 Vertragsschluss & Angebot" id="par-2">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Angebote des Auftragnehmers sind freibleibend, soweit nicht ausdrücklich als
                verbindlich gekennzeichnet.
              </ListItem>
              <ListItem num="(2)">
                Ein Vertrag kommt durch schriftliche Auftragsbestätigung des Auftragnehmers
                oder durch tatsächliche Leistungserbringung zustande. Mündliche Nebenabreden
                bedürfen zu ihrer Wirksamkeit der Schriftform.
              </ListItem>
              <ListItem num="(3)">
                Die Anfrage über das Kontaktformular auf dieser Website stellt kein Angebot
                im Rechtssinne dar, sondern dient lediglich der Vorbereitung eines individuellen
                Angebots.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 3 Leistungsumfang" id="par-3">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Der Auftragnehmer erbringt Leistungen im Bereich der zerstörungsfreien
                Ultraschall-Leckageortung an Druckluft- und Prozessgasanlagen, insbesondere:
                Vor-Ort-Audits, akustische Visualisierung mit Ultraschallkamera, quantitative
                Bewertung von Leckageverlusten in m³/h und €/Jahr, Erstellung von
                Energieeffizienz-Berichten gem. ISO 50001.
              </ListItem>
              <ListItem num="(2)">
                Der genaue Leistungsumfang ergibt sich aus dem jeweiligen Angebot bzw. der
                Auftragsbestätigung. Mündlich nicht ausdrücklich vereinbarte Leistungen sind
                nicht geschuldet.
              </ListItem>
              <ListItem num="(3)">
                Soweit der Auftragnehmer Berichte und Auswertungen erstellt, gibt er diese
                nach bestem fachlichen Wissen ab. Eine Garantie für die wirtschaftliche
                Verwertung der Ergebnisse durch den Auftraggeber wird nicht übernommen.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 4 Mitwirkungspflichten des Auftraggebers" id="par-4">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Der Auftraggeber stellt dem Auftragnehmer für die Durchführung des Auftrags
                rechtzeitig und kostenfrei den Zutritt zu den zu untersuchenden Anlagenteilen,
                geeignete Sicherheitsausrüstung (sofern nicht vom Auftragnehmer mitgebracht)
                sowie eine fachkundige Kontaktperson zur Verfügung.
              </ListItem>
              <ListItem num="(2)">
                Der Auftraggeber stellt sicher, dass die zu untersuchenden Anlagen während des
                Audits unter Betriebsdruck stehen.
              </ListItem>
              <ListItem num="(3)">
                Verzögerungen, die aus einer Verletzung der Mitwirkungspflichten resultieren,
                gehen zulasten des Auftraggebers.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 5 Vergütung & Zahlungsbedingungen" id="par-5">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Die Vergütung für das Full-Service-Abo sowie für Einzelleistungen ergibt sich
                aus dem individuellen Angebot bzw. der Auftragsbestätigung. Alle Preise verstehen
                sich netto zuzüglich der jeweils gültigen gesetzlichen Umsatzsteuer.
              </ListItem>
              <ListItem num="(2)">
                Rechnungen sind binnen <strong>14 Tagen</strong> nach Rechnungsdatum ohne Abzug
                zur Zahlung fällig.
              </ListItem>
              <ListItem num="(3)">
                Bei Zahlungsverzug ist der Auftragnehmer berechtigt, Verzugszinsen in Höhe von
                9 Prozentpunkten über dem Basiszinssatz gemäß § 288 Abs. 2 BGB zu berechnen.
                Die Geltendmachung eines weitergehenden Schadens bleibt vorbehalten.
              </ListItem>
              <ListItem num="(4)">
                Die Aufrechnung mit Gegenansprüchen des Auftraggebers oder die Zurückbehaltung
                von Zahlungen wegen solcher Ansprüche sind nur zulässig, soweit die
                Gegenansprüche unbestritten oder rechtskräftig festgestellt sind.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 6 Vertragslaufzeit & Kündigung (Full-Service-Abo)" id="par-6">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Das Full-Service-Abo hat eine <strong>Mindestlaufzeit von 24 Monaten</strong>,
                beginnend mit dem ersten Vor-Ort-Audit.
              </ListItem>
              <ListItem num="(2)">
                Erfolgt keine ordentliche Kündigung mit einer Frist von 3 Monaten zum Ende
                der Mindestlaufzeit, verlängert sich der Vertrag automatisch um jeweils
                weitere 12 Monate.
              </ListItem>
              <ListItem num="(3)">
                Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt
                beiden Vertragsparteien unberührt.
              </ListItem>
              <ListItem num="(4)">
                Kündigungen bedürfen der Textform (E-Mail genügt).
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 7 Mängelansprüche & Gewährleistung" id="par-7">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Der Auftragnehmer erbringt seine Leistungen mit der Sorgfalt eines ordentlichen
                Kaufmanns nach dem allgemein anerkannten Stand der Technik. Erkennbare Mängel
                sind unverzüglich, spätestens innerhalb von 14 Tagen nach Leistungserbringung,
                schriftlich anzuzeigen.
              </ListItem>
              <ListItem num="(2)">
                Bei berechtigten Mängelrügen wird der Auftragnehmer zunächst Nacherfüllung
                leisten. Schlägt diese fehl, ist der Auftraggeber berechtigt, die Vergütung
                angemessen zu mindern oder vom Vertrag zurückzutreten.
              </ListItem>
              <ListItem num="(3)">
                Gewährleistungsansprüche verjähren in einem Jahr ab Leistungserbringung,
                soweit nicht gesetzlich zwingend eine längere Frist vorgeschrieben ist.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 8 Haftung" id="par-8">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Der Auftragnehmer haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit
                sowie nach den Vorschriften des Produkthaftungsgesetzes und für Schäden aus
                der Verletzung des Lebens, des Körpers oder der Gesundheit.
              </ListItem>
              <ListItem num="(2)">
                Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten
                (Kardinalpflichten) ist die Haftung auf den vertragstypischen, vorhersehbaren
                Schaden begrenzt, höchstens jedoch auf die Höhe der vertraglich vereinbarten
                Jahresvergütung.
              </ListItem>
              <ListItem num="(3)">
                Eine darüber hinausgehende Haftung – insbesondere für entgangenen Gewinn,
                mittelbare Schäden und Folgeschäden – ist ausgeschlossen.
              </ListItem>
              <ListItem num="(4)">
                Der Auftragnehmer haftet nicht für Schäden, die dadurch entstehen, dass der
                Auftraggeber die Audit-Ergebnisse nicht oder verspätet umsetzt.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 9 Geheimhaltung" id="par-9">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Beide Vertragsparteien verpflichten sich, alle im Rahmen der Vertragsbeziehung
                erlangten betrieblichen Informationen, Daten und Unterlagen der jeweils anderen
                Partei vertraulich zu behandeln und nur für Zwecke der Vertragsdurchführung zu
                verwenden.
              </ListItem>
              <ListItem num="(2)">
                Die Geheimhaltungspflicht besteht für 5 Jahre nach Vertragsende fort, soweit
                die Informationen nicht offenkundig sind.
              </ListItem>
            </ol>
          </Section>

          <Section title="§ 10 Datenschutz" id="par-10">
            <p>
              Der Auftragnehmer verarbeitet personenbezogene Daten des Auftraggebers im Rahmen
              der gesetzlichen Bestimmungen, insbesondere der DSGVO. Einzelheiten sind in der
              <Link to="/datenschutz" className="text-[#0891b2] hover:underline"> Datenschutzerklärung </Link>
              geregelt.
            </p>
          </Section>

          <Section title="§ 11 Schlussbestimmungen" id="par-11">
            <ol className="list-none space-y-2">
              <ListItem num="(1)">
                Es gilt ausschließlich das Recht der Bundesrepublik Deutschland unter Ausschluss
                des UN-Kaufrechts.
              </ListItem>
              <ListItem num="(2)">
                Ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem Vertrag ist
                München, sofern der Auftraggeber Kaufmann, juristische Person des öffentlichen
                Rechts oder öffentlich-rechtliches Sondervermögen ist.
              </ListItem>
              <ListItem num="(3)">
                Erfüllungsort für beide Vertragsparteien ist München.
              </ListItem>
              <ListItem num="(4)">
                Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein,
                bleibt die Wirksamkeit der übrigen Bestimmungen davon unberührt
                (salvatorische Klausel).
              </ListItem>
            </ol>
          </Section>

          <div className="py-10 mt-6 border-t border-slate-200/70 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
